<script setup lang="ts">
import { usePageField } from '@voix/composables/usePageField'
import { usePageStore } from '@voix/store/pageStore'

defineLayout({
  label: 'Atlantisbahamas',

  colors: {
    fields: {
      picker: true,
      palette: true,
    },
    background: {
      picker: true,
      palette: true,
    },
    foreground: {
      picker: true,
      palette: true,
    },
  },
  colorPalette: {
    '#3957af': {
      fields: true,
      background: true,
      foreground: true,
    },
    '#254297': {
      fields: true,
      background: true,
      foreground: true,
    },
    '#1fb0d7': {
      fields: true,
      background: true,
      foreground: true,
    },
    '#EB897C': {
      fields: true,
      background: true,
      foreground: true,
    },
  },
  grid: {
    default: {
      container: false,
      columns: 6,
      gutter: '3rem',
    },
    lg: {
      container: true,
      columns: 12,
      gap: '4rem', // .gap-8
      gutter: '2rem',
    },
  },
})

useAtlantisHead()

// Generated much of this at https://realfavicongenerator.net
useHead({
  htmlAttrs: {
    lang: 'en-US',
  },
  meta: [
    { name: 'msapplication-TileColor', content: '#000000' },
    { name: 'msapplication-config', content: '/favicons/atlantisbahamas/browserconfig.xml' },
    { name: 'theme-color', content: '#000000' },
  ],
  link: [
    { rel: 'apple-touch-icon', sizes: '180x180', href: '/favicons/atlantisbahamas/apple-touch-icon.png' },
    { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicons/atlantisbahamas/favicon-32x32.png' },
    { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicons/atlantisbahamas/favicon-16x16.png' },
    { rel: 'manifest', href: '/favicons/atlantisbahamas/site.webmanifest' },
    { rel: 'mask-icon', href: '/favicons/atlantisbahamas/safari-pinned-tab.svg', color: '#5bbad5' },
    { rel: 'shortcut icon', href: '/favicons/atlantisbahamas/favicon.ico' },
  ],
  script: [
    { src: '//fast.wistia.com/assets/external/E-v1.js', async: true },
  ],
})

const route = useRoute()
const pageStore = usePageStore()
const isVoixTemplatizer = inject('isVoixTemplatizer', false)

const pageField = usePageField()
const hidePopup = pageField.get('hidePopup', false)
const hideNavigation = pageField.get('hideNavigation', false)
const hideFooter = pageField.get('hideFooter', false)

const footerNewsletterSignupTopBackgroundColor = pageField.get('footerNewsletterSignupTopBackgroundColor')
const footerNewsletterSignupBackgroundColor = pageField.get('footerNewsletterSignupBackgroundColor')
const footerNewsletterSignupInputBackgroundColor = pageField.get('footerNewsletterSignupInputBackgroundColor')
const footerNewsletterSignupInputColor = pageField.get('footerNewsletterSignupInputColor')
const footerNewsletterSignupButtonBackgroundColor = pageField.get('footerNewsletterSignupButtonBackgroundColor')
const footerNewsletterSignupButtonColor = pageField.get('footerNewsletterSignupButtonColor')

const siteId = pageStore.currentPage?.site_id || 1

const { data: layoutData }: { data: Record<string, any> } = await useVoixAsyncData(
  `/api/atlantis/sites/${siteId}/layout-data/Atlantisbahamas`,
)

const mainMenu = layoutData.value?.mainMenu || {}
const footerMenu = layoutData.value?.footerMenu || {}
const legalMenu = layoutData.value?.legalMenu || {}

const nochrome = route.query.nochrome

const onCasino = computed(() => {
  return route.path.startsWith('/casino')
})

onMounted(() => {
  // Error Logs ERROR_MESSAGE or ERROR_MESSAGENO_ROOMS_FOUND
  if (process.client) {
    try {
      const params = new URLSearchParams(window.location.search)
      if (params.has('ERROR_MESSAGE') || params.has('ERROR_MESSAGENO_ROOMS_FOUND')) {
        const bookingSubmittionCookie = useCookie('booking-submission-id')
        const bookingSubmittionFrom = useCookie('booking-submitted-from')

        if (bookingSubmittionCookie.value)
          $voixFetch(`/user-booking-redirected?submission_id=${bookingSubmittionCookie.value}&from=${bookingSubmittionFrom.value}`)

        bookingSubmittionCookie.value = null
        bookingSubmittionFrom.value = null
      }
    }
    catch (e) {
      console.log('error', e)
    }
  }
})
</script>

<template>
  <div
    id="app"
    class="relative atlantis-theme"
    :class="{
      'min-h-screen': !isVoixTemplatizer,
    }"
  >
    <div
      id="glue"
      class="text-zinc-700 bg-orange-100"
      :class="{ nochrome }"
    >
      <div class="relative z-40">
        <div id="portal-modal" />
      </div>

      <div class="flex justify-center">
        <div class="relative z-10 w-full">
          <div
            v-if="(!nochrome && pageStore.currentPage?.site_id !== 4) && !hideNavigation.value && !isVoixTemplatizer"
          >
            <!-- TODO: Missing ta_track_num -->
            <AtlantisbahamasDesktopNavigation
              class="hidden min-[1366px]:block"
              :menu="mainMenu"
            />
            <AtlantisbahamasMobileNavigation
              class="min-[1366px]:hidden"
              :menu="mainMenu"
            />
          </div>

          <div
            class="relative "
            :class="{
              'min-h-[50vh]': !isVoixTemplatizer,
            }"
          >
            <slot />

            <SlicesGlueSpecialsSpecialsSlideOut
              v-if="!onCasino && pageStore.currentPage?.site_id === 1"
              :fields="{
                position: { value: 'right' },
                specialId: { value: 609 },
                openLabel: { value: 'Last Minute Getaway' },
                showBookNow: { value: true },
                openOnLoad: { value: false },
              }"
            />
          </div>

          <div
            v-if="!nochrome && !isVoixTemplatizer"
            class="relative"
          >
            <AtlantisbahamasFooter
              v-if="footerMenu && legalMenu && !hideFooter.value"
              :menu="footerMenu"
              :legal-menu="legalMenu"
              :top-background-color="footerNewsletterSignupTopBackgroundColor.value"
              :background-color="footerNewsletterSignupBackgroundColor.value"
              :input-background-color="footerNewsletterSignupInputBackgroundColor.value"
              :input-color="footerNewsletterSignupInputColor.value"
              :button-background-color="footerNewsletterSignupButtonBackgroundColor.value"
              :button-color="footerNewsletterSignupButtonColor.value"
            />
            <AtlantisbahamasAlert />
          </div>
        </div>
      </div>
      <GlobalUIHelpBot v-if="!isVoixTemplatizer" />
      <ClientOnly>
        <GlobalUIPopupForm
          v-if="!hidePopup.value"
          form="popup-general-newsletter"
          image="/imgs/newsletter-signup-form/atlantisbahamas.jpg"
          title="SIGN UP FOR SPECIAL OFFERS AND MORE"
          terms="You agree to receive marketing emails from us. You can opt out at any time"
          travel-agent="Are you a travel agent?"
        />
      </ClientOnly>
    </div>
  </div>
</template>

<style lang="scss">
@use '~/assets/sass/atlantisbahamas.scss';
</style>
